import { create } from 'zustand';

/**
 * WebSocket telemetry store.
 *
 * Holds per-channel status + the most recent message payload
 * plus a bounded tail buffer (handy for debugging latency and
 * pow­ers the Activity view in Wave 2).
 */

export type WsChannel =
  | 'agent-activity'
  | 'phase-stream'
  | 'checkpoint-events'
  | 'metrics'
  | 'approvals';

export type WsConnectionStatus = 'connecting' | 'open' | 'closed' | 'errored';

export interface WsMessage {
  receivedAt: number;
  payload: unknown;
}

export interface WsChannelState {
  status: WsConnectionStatus;
  error?: string;
  lastMessage: WsMessage | null;
  tail: WsMessage[];
  changedAt: number;
}

export interface WsAggregate {
  status: WsConnectionStatus;
  lastChange: number;
}

interface WsStoreState {
  connection: WsAggregate;
  channels: Record<WsChannel, WsChannelState>;
  setStatus: (channel: WsChannel, status: WsConnectionStatus, error?: string) => void;
  pushMessage: (channel: WsChannel, msg: WsMessage) => void;
  setConnection: (aggregate: WsAggregate) => void;
  clearChannel: (channel: WsChannel) => void;
}

const MAX_TAIL = 50;

const emptyChannel = (): WsChannelState => ({
  status: 'closed',
  lastMessage: null,
  tail: [],
  changedAt: Date.now(),
});

const initialChannels: Record<WsChannel, WsChannelState> = {
  'agent-activity': emptyChannel(),
  'phase-stream': emptyChannel(),
  'checkpoint-events': emptyChannel(),
  metrics: emptyChannel(),
  approvals: emptyChannel(),
};

export const useWsStore = create<WsStoreState>()((set) => ({
  connection: { status: 'closed', lastChange: Date.now() },
  channels: initialChannels,
  setStatus: (channel, status, error) =>
    set((prev) => ({
      channels: {
        ...prev.channels,
        [channel]: {
          ...prev.channels[channel],
          status,
          ...(error !== undefined ? { error } : { error: undefined }),
          changedAt: Date.now(),
        },
      },
    })),
  pushMessage: (channel, msg) =>
    set((prev) => {
      const existing = prev.channels[channel];
      const tail = [...existing.tail, msg].slice(-MAX_TAIL);
      return {
        channels: {
          ...prev.channels,
          [channel]: {
            ...existing,
            lastMessage: msg,
            tail,
          },
        },
      };
    }),
  setConnection: (aggregate) => set({ connection: aggregate }),
  clearChannel: (channel) =>
    set((prev) => ({
      channels: { ...prev.channels, [channel]: emptyChannel() },
    })),
}));

// ── K3.2 + M1.3 (round-5) — /ws/approvals subscription helper ──────────────

export type ApprovalEvent =
  | { type: 'approval.decided'; approval_id: string; decision: string; actor: string }
  | { type: 'approval.new'; approval: import('../api/types').ApprovalSummary };

/**
 * Open a WebSocket to `/ws/approvals` and invoke `onEvent` for each
 * typed payload that matches the Approvals realtime schema. Returns
 * a cleanup function that tears down the connection + timers.
 *
 * M1.3 (round-5) — production-grade resilience:
 *   - Exponential backoff on reconnect (250ms → 8s cap).
 *   - Heartbeat ping every 20s; missed pong for 2 intervals triggers
 *     a forced reconnect so a half-open TCP never silently orphans the
 *     queue.
 *   - Buffer overflow guard: we don't queue more than 1000 pending
 *     events while the socket is down; the newest 1000 win, older
 *     events drop with a single console.warn so operators notice.
 *   - Clean teardown on the returned disposer: clears all timers,
 *     flips the cancelled flag, closes the socket without reconnect.
 */
/**
 * #118 (round-6 close-out) — mint a short-lived WS ticket bound to the
 * authenticated (org, optional project). The token is appended to the
 * /ws/* URL as `?token=…`. The server's WS upgrade handler validates
 * it against the in-memory store populated by POST /v1/auth/ws-ticket
 * and then enforces `ShaktiEvent::visible_to` fan-out.
 */
async function mintWsToken(): Promise<string | null> {
  try {
    const { useAuthStore } = await import('./auth');
    const auth = useAuthStore.getState();
    const orgId = auth.organizationId;
    if (!orgId) return null;
    const { shaktiApi } = await import('../api/client');
    const { useProjectStore } = await import('./project');
    const project = useProjectStore.getState().activeProjectId ?? undefined;
    const resp = await shaktiApi.mintWsTicket({
      organization_id: orgId,
      ...(project ? { project_id: project } : {}),
    });
    return resp.token;
  } catch {
    return null;
  }
}

async function resolveScopedUrl(base: string): Promise<string> {
  const token = await mintWsToken();
  const { useAuthStore } = await import('./auth');
  const org = useAuthStore.getState().organizationId;
  const { useProjectStore } = await import('./project');
  const project = useProjectStore.getState().activeProjectId;
  const params = new URLSearchParams();
  if (org) params.set('org', org);
  if (project) params.set('project', project);
  if (token) params.set('token', token);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

export function subscribeApprovals(onEvent: (event: ApprovalEvent) => void): () => void {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const baseUrl = `${proto}//${window.location.host}/ws/approvals`;

  const MAX_BUFFER = 1000;
  const HEARTBEAT_MS = 20_000;
  const HEARTBEAT_TIMEOUT_MS = HEARTBEAT_MS * 2;

  let ws: WebSocket | null = null;
  let cancelled = false;
  let reconnectDelayMs = 250;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  let lastPongAt = Date.now();
  const eventBuffer: ApprovalEvent[] = [];

  function clearTimers(): void {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  }

  function flushBuffer(): void {
    while (eventBuffer.length > 0) {
      const evt = eventBuffer.shift();
      if (evt) onEvent(evt);
    }
  }

  function scheduleReconnect(): void {
    if (cancelled) return;
    if (reconnectTimer) return;
    const jitter = Math.random() * 250;
    const delay = Math.min(reconnectDelayMs + jitter, 8_000);
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      reconnectDelayMs = Math.min(reconnectDelayMs * 2, 8_000);
      connect();
    }, delay);
  }

  function startHeartbeat(): void {
    heartbeatTimer = setInterval(() => {
      if (cancelled || !ws) return;
      // Server echoes a `type: "ping"` frame as `type: "pong"`; missing
      // a pong for 2×interval means the connection is half-open.
      if (Date.now() - lastPongAt > HEARTBEAT_TIMEOUT_MS) {
        try {
          ws.close();
        } catch {
          /* already closed */
        }
        return;
      }
      try {
        ws.send(JSON.stringify({ type: 'ping' }));
      } catch {
        /* socket already broken; close handler will reconnect */
      }
    }, HEARTBEAT_MS);
  }

  function connect(): void {
    if (cancelled) return;
    // #118 — resolve a scoped URL (?org=…&project=…&token=…) each
    // reconnect so a rotated ticket never ends up cached in a stale
    // WebSocket URL.
    void resolveScopedUrl(baseUrl)
      .then((url) => {
        if (cancelled) return;
        try {
          ws = new WebSocket(url);
        } catch {
          scheduleReconnect();
          return;
        }
        attachSocketHandlers();
      })
      .catch(() => {
        scheduleReconnect();
      });
  }

  function attachSocketHandlers(): void {
    if (!ws) return;

    ws.addEventListener('open', () => {
      if (cancelled) return;
      lastPongAt = Date.now();
      reconnectDelayMs = 250;
      startHeartbeat();
      flushBuffer();
    });

    ws.addEventListener('message', (ev) => {
      if (cancelled) return;
      lastPongAt = Date.now();
      try {
        const raw = typeof ev.data === 'string' ? ev.data : '';
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || typeof parsed.type !== 'string') return;
        // Heartbeat echoes from the server — discard before the caller sees them.
        if (parsed.type === 'pong' || parsed.type === 'ping') return;
        const evt = parsed as ApprovalEvent;
        // Buffer overflow guard while the socket is open only matters
        // if the caller's onEvent throws; we invoke directly.
        try {
          onEvent(evt);
        } catch (err) {
          console.error('subscribeApprovals: onEvent threw', err);
        }
      } catch {
        /* malformed frame — ignore */
      }
    });

    const handleClose = (): void => {
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
      if (cancelled) return;
      // Record a synthetic "buffer bookmark" so we know we dropped
      // whatever happened between close and the next open. Future
      // versions can reconcile via a /v1/approvals?since= query on
      // reconnect; round-5 leaves reconciliation to the consumer.
      if (eventBuffer.length > MAX_BUFFER) {
        const dropped = eventBuffer.length - MAX_BUFFER;
        eventBuffer.splice(0, dropped);
        console.warn(
          `subscribeApprovals: dropped ${dropped} buffered events (> ${MAX_BUFFER}); newest retained`,
        );
      }
      scheduleReconnect();
    };

    ws.addEventListener('close', handleClose);
    ws.addEventListener('error', () => {
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
    });
  }

  connect();

  return () => {
    cancelled = true;
    clearTimers();
    try {
      ws?.close();
    } catch {
      /* already closed */
    }
  };
}
